/**
 * GovFeesBox — dynamic list of government-authority fees with per-row
 * receipt upload + Gemini OCR extraction.
 *
 * iter-264c (Feb 2026) — Per user request each fee now carries:
 *   • title (اسم/مسمى الرسم) — required text label
 *   • notes (ملاحظات) — optional free-form remarks
 *   • extras[]           — optional additional attachments (no AI check)
 * The primary receipt is still required and still AI-verified.
 *
 * The Save button uses the shared SmartSaveButton so it flips into a
 * green "✓ تم الحفظ" pill after saving and reverts to "حفظ" on any edit.
 */
import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Landmark, Loader2, Upload, Plus, X, Paperclip, Wallet } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { SectionCard, SmartSaveButton, ReceiptDownloadLink } from './ui';
import { fileToDataUrl, extractReceipt } from './helpers';

const MAX_EXTRAS_PER_FEE   = 5;
const MAX_EXTRA_MB         = 8;
const EXTRA_MIME_RE        = /^(application\/pdf|image\/(png|jpe?g|webp|heic|heif))$/i;
const rid = () => Math.random().toString(36).slice(2);

// Map a stored gov-fee entry (from `reg.gov_fees.entries`) into the
// runtime shape used by the form (adds transient AI / scan flags).
const seedEntry = (e = {}) => ({
  id:               rid(),
  title:            e.title || '',
  notes:            e.notes || '',
  amount:           e.amount || '',
  reference_number: e.reference_number || '',
  receipt_url:      e.receipt_url || '',
  receipt_mime:     e.receipt_mime || '',
  ai_verified:      !!e.ai_verified,
  // iter-264e — paid_to per row ('to_gov' | 'to_us' | null).
  paid_to:          e.paid_to || null,
  extras: Array.isArray(e.extras) ? e.extras.map((x) => ({
    id:   rid(),
    name: x.name || '',
    mime: x.mime || '',
    size: x.size || 0,
    data_url: x.data_url || '',
  })) : [],
  ai_info:  '',
  scanning: false,
});

// Strip the runtime-only keys before persisting.
const clean = (e) => ({
  title:            (e.title || '').trim(),
  notes:            (e.notes || '').trim(),
  amount:           parseFloat(e.amount) || 0,
  reference_number: e.reference_number,
  receipt_url:      e.receipt_url,
  receipt_mime:     e.receipt_mime,
  ai_verified:      e.ai_verified,
  paid_to:          e.paid_to || null,
  extras: (e.extras || []).map((x) => ({
    name: x.name, mime: x.mime, size: x.size, data_url: x.data_url,
  })),
});

export function GovFeesBox({ reg, onSave, t, saving }) {
  const g = reg.gov_fees || {};
  const govFeesSaved = !!(reg.gov_fees && ('has_other_fees' in reg.gov_fees));
  const [hasOther, setHasOther] = useState(govFeesSaved ? !!g.has_other_fees : null);
  const [entries, setEntries] = useState(() =>
    Array.isArray(g.entries) ? g.entries.map(seedEntry) : []
  );

  const addRow = () => setEntries((p) => [...p, seedEntry()]);
  const removeRow = (id) => setEntries((p) => p.filter((r) => r.id !== id));
  const updateRow = (id, patch) => setEntries((p) => p.map((r) => r.id === id ? { ...r, ...patch } : r));

  // iter-299h (Feb 2026) — Track the exact payload shape we last saw from
  // the server. Previously we tried a `useRef` + `useEffect` combo, but
  // ref writes do NOT trigger a re-render; on the save render, `dirty`
  // would read the OLD baseline and stay true forever (the useEffect
  // only updated the ref AFTER render). Reading `serverSignature`
  // directly inside the `dirty` memo makes the baseline track `reg`
  // in real time.
  const serverSignature = useMemo(() => {
    if (!govFeesSaved) return null;
    return JSON.stringify({
      has_other_fees: !!g.has_other_fees,
      entries: Array.isArray(g.entries) ? g.entries.map(clean) : [],
    });
  }, [govFeesSaved, g.has_other_fees, g.entries]);

  const handleReceipt = async (id, file) => {
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    updateRow(id, { receipt_url: dataUrl, receipt_mime: file.type, scanning: true, ai_info: '', ai_verified: false });
    try {
      const data = await extractReceipt(dataUrl, file.type);
      if (!data.is_receipt) {
        updateRow(id, { scanning: false, ai_info: t('❌ الملف ليس إيصالاً: ', '❌ Not a receipt: ') + (data.reject_reason || ''), ai_verified: false });
        toast.error(t('الملف ليس إيصالاً', 'File is not a receipt'));
        return;
      }
      updateRow(id, {
        scanning: false,
        ai_verified: true,
        ai_info: t('✓ AI قرأ الإيصال', '✓ AI read receipt'),
        amount: data.amount ? String(data.amount) : '',
        reference_number: data.reference || '',
      });
      toast.success(t('✓ تم قراءة الإيصال', '✓ Receipt scanned'));
    } catch (e) {
      updateRow(id, { scanning: false });
      toast.error(e?.response?.data?.detail || t('فشل قراءة الإيصال', 'OCR failed'));
    }
  };

  // iter-264c — Optional per-fee attachments (NO AI validation).
  const addExtras = async (id, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const row = entries.find((r) => r.id === id);
    if (!row) return;
    if ((row.extras?.length || 0) + files.length > MAX_EXTRAS_PER_FEE) {
      toast.error(t(`الحد الأقصى ${MAX_EXTRAS_PER_FEE} مرفقات إضافية`, `Max ${MAX_EXTRAS_PER_FEE} extra attachments`));
      return;
    }
    const accepted = [];
    for (const f of files) {
      if (!EXTRA_MIME_RE.test(f.type)) { toast.error(t(`${f.name}: نوع غير مدعوم`, `${f.name}: unsupported type`)); continue; }
      if (f.size > MAX_EXTRA_MB * 1024 * 1024) { toast.error(t(`${f.name}: أكبر من ${MAX_EXTRA_MB}MB`, `${f.name}: exceeds ${MAX_EXTRA_MB}MB`)); continue; }
      const data_url = await fileToDataUrl(f);
      accepted.push({ id: rid(), name: f.name, mime: f.type, size: f.size, data_url });
    }
    if (accepted.length) {
      updateRow(id, { extras: [...(row.extras || []), ...accepted] });
    }
  };
  const removeExtra = (rowId, extraId) => {
    const row = entries.find((r) => r.id === rowId);
    if (!row) return;
    updateRow(rowId, { extras: (row.extras || []).filter((x) => x.id !== extraId) });
  };

  // Dirty tracking — iter-299h: compare the current form shape against
  // the server signature directly (not through a ref — refs don't trigger
  // re-renders, which left dirty stuck true after a save).
  const dirty = useMemo(() => {
    const currentSig = JSON.stringify({
      has_other_fees: hasOther === null ? null : !!hasOther,
      entries: entries.map(clean),
    });
    if (serverSignature === null) {
      // Never saved yet in this session — the card is dirty IFF the
      // operator has entered anything at all.
      return hasOther !== null || entries.length > 0;
    }
    return currentSig !== serverSignature;
  }, [hasOther, entries, serverSignature]);

  const save = () => {
    if (hasOther === null) {
      toast.error(t('اختر: هل يوجد رسوم للجهات الحكومية الأخرى؟', 'Pick: are there other government fees?'));
      return;
    }
    if (hasOther) {
      if (entries.length === 0) {
        toast.error(t('أضف رسماً واحداً على الأقل', 'Add at least one fee'));
        return;
      }
      for (let i = 0; i < entries.length; i += 1) {
        const e = entries[i];
        if (!e.title || !e.title.trim())                    { toast.error(t(`الرسم ${i + 1}: مسمى الرسم مطلوب`, `Fee ${i + 1}: title required`)); return; }
        if (!e.receipt_url)                                 { toast.error(t(`الرسم ${i + 1}: الإيصال مطلوب`, `Fee ${i + 1}: receipt required`)); return; }
        if (!e.amount || parseFloat(e.amount) <= 0)         { toast.error(t(`الرسم ${i + 1}: المبلغ مطلوب`, `Fee ${i + 1}: amount required`)); return; }
        if (!e.reference_number || !e.reference_number.trim()) { toast.error(t(`الرسم ${i + 1}: رقم المرجع مطلوب`, `Fee ${i + 1}: reference required`)); return; }
        // iter-264e — paid_to now mandatory per row.
        if (!e.paid_to) {
          toast.error(t(`الرسم ${i + 1}: اختر مسدد لجهة حكومية أو مسدد لنا`, `Fee ${i + 1}: pick "paid to government" or "paid to us"`));
          return;
        }
      }
    }
    onSave({
      gov_fees: {
        has_other_fees: hasOther,
        entries: hasOther ? entries.map(clean) : [],
      },
    });
  };

  return (
    <SectionCard icon={Landmark} iconColor="#06b6d4" title={t('رسوم الجهات الحكومية', 'Government fees')}>
      <div className="mb-4">
        <label className="block text-sm text-white mb-3">
          {t('هل يوجد رسوم للجهات الحكومية الأخرى؟', 'Are there other government fees?')}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setHasOther(false)}
            className={
              'p-3 rounded-xl border-2 text-sm font-semibold transition-all ' +
              (hasOther === false ? 'bg-[#0a0a0a] border-red-500 text-red-300' : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
            }
            data-testid="govfees-no-btn"
          >
            {t('لا', 'No')}
          </button>
          <button
            type="button"
            onClick={() => {
              setHasOther(true);
              setEntries((prev) => (prev.length === 0 ? [seedEntry()] : prev));
            }}
            className={
              'p-3 rounded-xl border-2 text-sm font-semibold transition-all ' +
              (hasOther === true ? 'bg-cyan-500/15 border-cyan-500 text-cyan-200' : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
            }
            data-testid="govfees-yes-btn"
          >
            {t('نعم', 'Yes')}
          </button>
        </div>
      </div>

      {hasOther === true ? (
        <>
          <div className="space-y-3">
            {entries.map((row, idx) => (
              <div key={row.id} className="p-3 rounded-xl bg-[#0a0a0a] border border-[#2a2a4e]">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold text-white">
                    {t(`رسم رقم ${idx + 1}`, `Fee #${idx + 1}`)}
                    {row.ai_verified ? <span className="ms-2 text-xs text-emerald-400">✓ AI</span> : null}
                  </div>
                  <button
                    onClick={() => removeRow(row.id)}
                    className="w-7 h-7 rounded-lg bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center"
                    data-testid={`govfees-remove-${idx}`}
                  >
                    <X className="h-3.5 w-3.5 text-red-400" />
                  </button>
                </div>
                {/* iter-264c — Title + Notes row (title required) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-[10px] text-[#71717A] mb-1">
                      {t('مسمى الرسم *', 'Fee title *')}
                    </label>
                    <Input
                      value={row.title}
                      onChange={(e) => updateRow(row.id, { title: e.target.value })}
                      placeholder={t('مثال: رسوم اشتراك الضريبة', 'e.g. Tax subscription fee')}
                      className="bg-black/40 border-[#2a2a4e] text-white rounded-lg text-sm h-9"
                      data-testid={`govfees-title-${idx}`}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[#71717A] mb-1">
                      {t('ملاحظات (اختياري)', 'Notes (optional)')}
                    </label>
                    <Input
                      value={row.notes}
                      onChange={(e) => updateRow(row.id, { notes: e.target.value })}
                      placeholder={t('ملاحظات على الرسم…', 'Any remarks…')}
                      className="bg-black/40 border-[#2a2a4e] text-white rounded-lg text-sm h-9"
                      data-testid={`govfees-notes-${idx}`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] text-[#71717A] mb-1">{t('إيصال *', 'Receipt *')}</label>
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-black/40 border border-[#2a2a4e] text-white cursor-pointer text-xs">
                      {row.scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-400" /> : <Upload className="h-3.5 w-3.5 text-cyan-400" />}
                      {row.scanning ? t('يقرأ...', 'Scan...') : row.receipt_url ? t('✓', '✓') : t('رفع', 'Upload')}
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleReceipt(row.id, e.target.files?.[0])} />
                    </label>
                  </div>
                  <div>
                    <label className="block text-[10px] text-[#71717A] mb-1">{t('المبلغ (د.إ) *', 'Amount (AED) *')}</label>
                    <Input type="number" value={row.amount} onChange={(e) => updateRow(row.id, { amount: e.target.value })} className="bg-black/40 border-[#2a2a4e] text-white rounded-lg text-sm h-9" dir="ltr" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[#71717A] mb-1">{t('رقم المرجع *', 'Reference *')}</label>
                    <Input value={row.reference_number} onChange={(e) => updateRow(row.id, { reference_number: e.target.value })} className="bg-black/40 border-[#2a2a4e] text-white rounded-lg text-sm h-9" dir="ltr" />
                  </div>
                </div>
                {row.ai_info ? <p className="text-[11px] mt-2" style={{ color: row.ai_verified ? '#10b981' : '#ef4444' }}>{row.ai_info}</p> : null}

                {row.receipt_url ? (
                  <ReceiptDownloadLink
                    url={row.receipt_url}
                    filename={`gov-fee-${idx + 1}-${reg.request_code || reg.id}${(row.receipt_mime || '').includes('pdf') ? '.pdf' : '.jpg'}`}
                    label={t(`إيصال الرسم ${idx + 1} المحفوظ`, `Saved receipt (Fee #${idx + 1})`)}
                    color="#06b6d4"
                    testId={`govfees-receipt-${idx}`}
                  />
                ) : null}

                {/* iter-264e — paid_to selector per row */}
                <div className="mt-3">
                  <label className="block text-[10px] text-[#71717A] mb-1">
                    {t('طريقة السداد *', 'Payment routing *')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => updateRow(row.id, { paid_to: 'to_gov' })}
                      className={
                        'p-2 rounded-lg border-2 text-[11px] font-semibold transition-all flex items-center gap-1 justify-center ' +
                        (row.paid_to === 'to_gov'
                          ? 'bg-blue-500/15 border-blue-500 text-blue-300'
                          : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
                      }
                      data-testid={`govfees-paid-to-gov-${idx}`}
                    >
                      <Landmark className="h-3 w-3" />
                      {t('مسدد لجهة حكومية', 'Paid to gov')}
                    </button>
                    <button
                      type="button"
                      onClick={() => updateRow(row.id, { paid_to: 'to_us' })}
                      className={
                        'p-2 rounded-lg border-2 text-[11px] font-semibold transition-all flex items-center gap-1 justify-center ' +
                        (row.paid_to === 'to_us'
                          ? 'bg-emerald-500/15 border-emerald-500 text-emerald-300'
                          : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
                      }
                      data-testid={`govfees-paid-to-us-${idx}`}
                    >
                      <Wallet className="h-3 w-3" />
                      {t('مسدد لنا', 'Paid to us')}
                    </button>
                  </div>
                </div>

                {/* iter-264c — Optional extra attachments per fee */}
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] text-white/60 flex items-center gap-1">
                      <Paperclip className="h-3 w-3" />
                      {t('مرفقات إضافية (اختياري)', 'Extra attachments (optional)')}
                    </div>
                    <label
                      htmlFor={`gov-extras-${row.id}`}
                      className="cursor-pointer inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-white/5 hover:bg-white/10 text-white/70 border border-white/10"
                      data-testid={`govfees-add-extra-${idx}`}
                    >
                      <Plus className="h-3 w-3" />
                      {t('إضافة مرفق', 'Add file')}
                    </label>
                    <input
                      id={`gov-extras-${row.id}`}
                      type="file"
                      multiple
                      accept="application/pdf,image/*"
                      className="hidden"
                      onChange={(e) => { addExtras(row.id, e.target.files); e.target.value = ''; }}
                    />
                  </div>
                  {(row.extras && row.extras.length > 0) ? (
                    <ul className="space-y-1">
                      {row.extras.map((x) => (
                        <li key={x.id} className="flex items-center gap-2 px-2 py-1 rounded-md bg-white/[0.02] border border-white/5 text-[11px] text-white/80"
                            data-testid={`govfees-extra-${idx}-${x.id}`}>
                          <Paperclip className="h-3 w-3 text-white/40" />
                          <span className="flex-1 truncate">{x.name}</span>
                          <button
                            onClick={() => removeExtra(row.id, x.id)}
                            className="w-5 h-5 rounded bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center"
                            data-testid={`govfees-remove-extra-${idx}-${x.id}`}
                          >
                            <X className="h-3 w-3 text-red-400" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[10px] text-white/30">
                      {t('لا مرفقات إضافية', 'No extra attachments')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={addRow}
            className="mt-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 text-sm"
            data-testid="govfees-add-row"
          >
            <Plus className="h-4 w-4" />
            {t('إضافة رسم', 'Add fee')}
          </button>
        </>
      ) : null}

      <div className="mt-4 text-end">
        <SmartSaveButton
          dirty={dirty}
          saving={saving}
          onClick={save}
          t={t}
          color="#06b6d4"
          hoverColor="#0891b2"
          data-testid="govfees-save"
        />
      </div>
    </SectionCard>
  );
}
